<!--
  @fileoverview Launcher Component - Main Chat Widget Toggle Button (Vue 3)

  The Launcher is the primary entry point for users to open the chat widget.
  It displays as a floating action button (FAB) that users click to open/close
  the chat window.

  @component Launcher
  @displayName Chat Launcher Button

  Features:
  - Animated click effect (ripple)
  - Responsive sizing (medium/large based on settings)
  - Shadow configuration
  - Auto-open based on session state
  - Max user limit handling (disabled state)
  - RTL layout support
  - Badge component for unread count

  @example
  <Launcher
    :isLauncherCanClick="true"
    :numberOfApps="1"
    @click="handleLauncherClick"
    @toggle-icons="handleToggleIcons"
  />
-->
<template>
  <div>
    <!-- Fade transition for smooth appearance/disappearance -->
    <transition name="el-fade-in">
      <div
        v-if="settings.title"
        :class="{
          effect,
          'keyreply-shadow': settings.launcherShadow !== false,
          'keyreply-medium': !device.mobile && settings.size !== 'large',
          'keyreply-large': !device.mobile && settings.size === 'large',
          'keyreply-disabled': store.webchatUsersIsAtMaxLimit,
        }"
        @click="launcherClicked"
        class="keyreply-launcher"
        id="kr-launcher"
      >
        <!-- Unread message badge overlay -->
        <Badge :count="store.unreadChatsCount" />
      </div>
    </transition>
  </div>
</template>

<script setup>
/**
 * @module components/Launcher
 * @description Main launcher button component for the webchat widget (Vue 3 Composition API)
 */
import { ref, watch, inject, computed } from 'vue'
import { useMainStore } from '@/stores/main'
import Badge from './Badge.vue'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import dayjs from 'dayjs'

dayjs.extend(advancedFormat)

/**
 * Component props
 * @property {boolean} isLauncherCanClick - Whether launcher is clickable (prevents double-clicks)
 * @property {number} numberOfApps - Number of enabled apps (from parent)
 */
const props = defineProps({
  isLauncherCanClick: {
    type: Boolean,
    default: true,
  },
  numberOfApps: {
    type: Number,
    default: 1,
  },
})

/**
 * Component emits
 * @event click - When launcher is successfully clicked
 * @event toggle-icons - When multi-app icons should be toggled (for multiple apps)
 */
const emit = defineEmits(['click', 'toggle-icons'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Device detection via provide/inject
 * @type {Object} Device info with mobile, iOS, iOSSDK, etc.
 */
const device = inject('device', {
  mobile: false,
  Mobile: false,
  iOS: false,
  iOSSDK: false,
  Android: false,
  Mac: false,
  isMobileQuery: false,
  ie: false,
})

/**
 * CSS class for click animation effect
 */
const effect = ref('')

/**
 * Computed access to settings from store
 */
const settings = computed(() => store.settings)

/**
 * Watch settings for auto-open functionality
 * When openAutomatically is enabled, checks if user was previously
 * in an active session and auto-opens the chat window.
 */
watch(
  () => store.settings,
  (after, before) => {
    if (!store.chatWindow && after && after.openAutomatically === true) {
      const lastOpenStatus = localStorage.getItem('keyreply-lastOpenStatus')
      const lastSeen = dayjs(localStorage.getItem('_krlastseen') || undefined)
      const now = dayjs()

      const isSameDay = now.isSame(lastSeen, 'day')

      // session_length is in minutes - check if within session window
      const isSameSession = now.diff(lastSeen, 'minutes') < store.settings.session_length

      // Auto-open if user had chat open in same session
      if (lastOpenStatus == 'true' && isSameSession) {
        launcherClicked()
      }
    }
  },
  { deep: true }
)

/**
 * Handle launcher button click
 *
 * Behavior depends on configuration:
 * - Single bot app: Opens chat window directly
 * - Multiple apps: Shows app icon selector
 *
 * Includes:
 * - Max user limit check
 * - Click effect animation
 * - iOS SDK handling
 *
 * @emits click - When launcher is successfully clicked
 * @emits toggle-icons - When multi-app icons should be toggled
 */
function launcherClicked() {
  if (!store.webchatUsersIsAtMaxLimit && props.isLauncherCanClick) {
    // Add ripple effect class
    effect.value = 'keyreply-show-effect'

    // Check if single bot app mode
    if (
      props.numberOfApps === 1 &&
      settings.value.apps.bot &&
      settings.value.apps.bot.enabled
    ) {
      // Single app - start chat directly
      store.start()
      store.toggleChatWindow(device.iOSSDK)
    } else {
      // Multiple apps - toggle app selector icons
      emit('toggle-icons')
    }

    // Remove effect class after animation completes
    setTimeout(() => {
      effect.value = ''
    }, 700)

    emit('click')
  }
}

/**
 * Expose launcherClicked method to parent via template ref
 * This allows parent component to programmatically trigger clicks
 */
defineExpose({
  launcherClicked,
})
</script>

<style scoped lang="scss">
.keyreply-launcher {
  transition: color 0.7s;
  -webkit-transition: color 0.7s;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  background-size: contain;
  cursor: pointer;
  position: static;

  width: 50px;
  height: 50px;

  pointer-events: auto;

  border-radius: var(--keyreply-launcher-border-radius);
  background-color: var(--keyreply-primary-color);
  background-image: var(--keyreply-launcher-background-image);

  &.keyreply-shadow {
    box-shadow: 0 8px 11px rgba(71, 69, 123, 0.25), 0 2px 4px rgba(71, 69, 123, 0.12);
  }

  &.keyreply-disabled {
    cursor: not-allowed;
  }

  &.keyreply-medium {
    width: 64px;
    height: 64px;
    bottom: 32px;
  }

  &.keyreply-large {
    width: 100px;
    height: 100px;
    max-height: 100px;
    max-width: 100px;
    min-height: 50px;
    bottom: 22px;

    @media only screen and (max-width: 380px) {
      width: 75px;
      height: 75px;
      max-height: 75px;
      max-width: 75px;
    }

    @media only screen and (max-width: 320px) {
      width: 50px;
      height: 50px;
      max-height: 50px;
      max-width: 50px;
    }
  }
}

[dir="ltr"] .keyreply-launcher {
  float: left;

  &.keyreply-medium {
    right: 26px;
  }

  &.keyreply-large {
    right: 18px;
  }
}

[dir="rtl"] .keyreply-launcher {
  float: right;

  &.keyreply-medium {
    left: 26px;
  }

  &.keyreply-large {
    left: 18px;
  }
}

.launcher-subtext {
  position: relative;
  top: 52px;
  text-align: center;
  font-size: 0.8em;
  transition: color 0.5s ease;

  &:hover {
    color: #0645ad;
    -webkit-transform: scale(1.3);
    -ms-transform: scale(1.3);
    transform: scale(1.3);
    text-decoration: underline;
  }
}

/* Click Effects */
.keyreply-effect:before {
  position: absolute;
  top: 50%;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  content: "";
  opacity: 0;
  filter: alpha(opacity=0);
  pointer-events: none;
}

[dir="ltr"] .keyreply-effect:before {
  left: 50%;
  margin: -50px 0 0 -50px;
}

[dir="rtl"] .keyreply-effect:before {
  right: 50%;
  margin: -50px -50px 0 0;
}

.keyreply-effect:before,
.keyreply-effect:after {
  box-shadow: 0 0 0 2px rgba(111, 148, 182, 0.6);
}

.keyreply-effect.keyreply-show-effect:before {
  -webkit-animation: keyreply-effect-1 0.5s forwards;
  animation: keyreply-effect-1 0.5s forwards;
}

.keyreply-effect.keyreply-show-effect:after {
  -webkit-animation: keyreply-effect-2 0.5s forwards;
  animation: keyreply-effect-2 0.5s forwards;
}

@-webkit-keyframes keyreply-effect-1 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.1, 1.1, 1);
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes keyreply-effect-1 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.1, 1.1, 1);
    transform: scale3d(1.1, 1.1, 1);
  }
}

@-webkit-keyframes keyreply-effect-2 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  50%,
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.2, 1.2, 1);
    transform: scale3d(1.2, 1.2, 1);
  }
}

@keyframes keyreply-effect-2 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  50%,
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.2, 1.2, 1);
    transform: scale3d(1.2, 1.2, 1);
  }
}
</style>
